import React, { useState, useEffect } from 'react';
import { applyPadKey, sanitizeQuantityInput } from '../utils/quantityInputParser';

// Modal dedicado a cotizaciones para no compartir comportamiento con compras.
const QuotationItemModal = ({
  isOpen,
  onClose,
  variety,
  quantities,
  closeModalVariety,
  closeModalProduct,
  handleQuantityChange,
  handleQuantityIncrement,
  setShowDiscountModal,
  discounts,
  edit,
  varietyPrice,
  canDiscount = true,
}) => {
  if (!isOpen || !variety) return null;

  // Usar el precio seleccionado o el primero como respaldo
  const currentPrice = varietyPrice || variety.VarietyPrices?.[0];
  if (!currentPrice) return null;
  if (currentPrice.price === null || currentPrice.price === undefined) return null;

  const [rawQuantity, setRawQuantity] = useState('0');
  const isKgUnit = /kg/i.test(String(currentPrice.metricUnit || '').toLowerCase());
  const priceIndex = variety.VarietyPrices.indexOf(currentPrice);

  useEffect(() => {
    const initialRaw = quantities[variety.id]?.[priceIndex];
    const initialValue = initialRaw === null || initialRaw === undefined
      ? '0'
      : String(initialRaw).replace(',', '.');
    setRawQuantity(sanitizeQuantityInput(initialValue, isKgUnit));
  }, [isKgUnit, priceIndex, quantities, variety.id]);

  const handleInputChange = (valueOrEvent) => {
    const nextValue = sanitizeQuantityInput(valueOrEvent, isKgUnit);
    setRawQuantity(nextValue);
    if (nextValue.endsWith('.')) {
      return;
    }
    handleQuantityChange(variety.id, priceIndex, nextValue);
  };

  const handlePadInput = (input) => {
    const nextValue = applyPadKey(rawQuantity, input, isKgUnit);
    setRawQuantity(nextValue);
    if (nextValue.endsWith('.')) {
      return;
    }
    handleQuantityChange(variety.id, priceIndex, nextValue);
  };

  const handleButtonChange = (increment) => {
    let numericValue = parseFloat(rawQuantity) || 0;
    numericValue += increment;
    const nextValue = isKgUnit
      ? (Math.max(numericValue, 0).toFixed(3).replace(/\.?0+$/, '') || '0')
      : String(Math.max(Math.trunc(numericValue), 0));
    setRawQuantity(nextValue);
    handleQuantityChange(variety.id, priceIndex, nextValue);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start overflow-y-auto pt-6"
      onClick={onClose}
    >
      <div
        className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full mx-4 relative max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-black bg-white p-1 rounded-full"
        >
          ✕
        </button>

        <div className="flex justify-center">
          <div className="relative w-[72px] h-[72px] sm:w-[90px] sm:h-[90px] mb-3 mt-1 sm:mb-4 sm:mt-2">
            <img
              src={currentPrice.priceImage || variety.varietyImage || 'https://placehold.co/200'}
              alt={variety.varietyName}
              className="absolute inset-0 w-full h-full object-cover rounded"
            />
          </div>
        </div>

        <div className="flex justify-between items-center mb-1">
          <h2 className="text-lg font-bold truncate mr-2">{variety.varietyName}</h2>
          <span className="text-sm text-gray-700">{currentPrice.metricUnit || ''}</span>
        </div>

        <p className="text-sm text-gray-700 mb-4 truncate">
          {(currentPrice.description || '').slice(0, 30)}
        </p>

        {currentPrice.isSoldOut && <p className="text-lg font-bold text-red-600">AGOTADO</p>}

        <div className="flex justify-between items-center mb-2">
          <p className="text-md font-bold">
            {discounts[variety.id]?.[priceIndex] ? (
              <>
                <span className="line-through text-gray-500 mr-2">${currentPrice.price.toLocaleString("De-de")}</span>
                <span>${(currentPrice.price - discounts[variety.id][priceIndex]).toLocaleString("De-de")}</span>
              </>
            ) : (
              `$${currentPrice.price.toLocaleString("De-de")}`
            )}
          </p>
          <p className="text-md font-semibold">
            Subtotal: ${Math.floor((currentPrice.price - ((discounts[variety.id]?.[priceIndex]) || 0)) * parseFloat(rawQuantity)).toLocaleString("De-de")}
          </p>
        </div>

        {edit && (
          <>
            <div className="flex items-center mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonChange(-1);
                }}
                className="w-10 h-10 bg-red-500 text-white rounded-l hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={Number.parseFloat(quantities[variety.id]?.[priceIndex] || 0) <= 0}
              >-</button>
              <input
                type="text"
                inputMode={isKgUnit ? 'decimal' : 'numeric'}
                value={rawQuantity}
                onChange={handleInputChange}
                className="text-center text-xl p-1 border-t border-b border-gray-300"
                style={{ width: `calc(100% - 96px)` }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonChange(1);
                }}
                className="w-10 h-10 bg-green-500 text-white rounded-r hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
              >+</button>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, '←', 'C'].map((key, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePadInput(key)}
                  className="py-3 bg-gray-100 text-xl rounded hover:bg-gray-200"
                >
                  {key}
                </button>
              ))}

              {/* Cerrar button inside pad */}
              {canDiscount && (
                <button
                  onClick={() => {setShowDiscountModal(true);}}
                  className={`col-span-1 py-3 text-white text-md rounded ${
                    parseFloat(rawQuantity) === 0
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                  disabled={parseFloat(rawQuantity) === 0}
                >
                  + Descuento
                </button>
              )}
              <button
                onClick={() => {
                  onClose();
                  closeModalVariety();
                  closeModalProduct();
                }}
                className="col-span-1 py-3 bg-blue-500 text-white text-lg rounded hover:bg-blue-600"
              >
                Cerrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuotationItemModal;
